import React, { useEffect } from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { loadWorkshopHome } from '../../ducks/workshop.duck';

import { Page, LayoutSingleColumn, NamedLink, ExternalLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import WorkshopSubnav from './WorkshopSubnav';
import css from './WorkshopPage.module.css';

// ─── Workshop imagery (studio-on-white set + hero/banner) ───────────────────
import heroImg from '../../assets/workshop/hero.jpg';
import ownerBannerImg from '../../assets/workshop/owner-guides-banner.jpg';
import imgCarHauler from '../../assets/workshop/trailer-car-hauler.jpg';
import imgDump from '../../assets/workshop/trailer-dump.jpg';
import imgEnclosed from '../../assets/workshop/trailer-enclosed.jpg';
import imgUtility from '../../assets/workshop/trailer-utility.jpg';
import imgEquipment from '../../assets/workshop/trailer-equipment.jpg';
import imgGooseneck from '../../assets/workshop/trailer-gooseneck.jpg';

import catHitches from '../../assets/workshop/cat-hitches-towing.jpg';
import catStraps from '../../assets/workshop/cat-straps-tie-downs.jpg';
import catLighting from '../../assets/workshop/cat-lighting-electrical.jpg';
import catTires from '../../assets/workshop/cat-tires-wheels.jpg';
import catToolboxes from '../../assets/workshop/cat-toolboxes-storage.jpg';
import catRamps from '../../assets/workshop/cat-ramps-loading.jpg';
import catSecurity from '../../assets/workshop/cat-security-tracking.jpg';
import catJacks from '../../assets/workshop/cat-jacks-stabilizers.jpg';
import catCovers from '../../assets/workshop/cat-covers-tarps.jpg';
import catHydraulics from '../../assets/workshop/cat-hydraulics-power.jpg';
import catMaintenance from '../../assets/workshop/cat-maintenance-safety.jpg';

import promoSecure from '../../assets/workshop/promo-secure.jpg';
import promoProtect from '../../assets/workshop/promo-protect.jpg';
import promoRoadready from '../../assets/workshop/promo-roadready.jpg';

// Local trailer-type art keyed by slug (falls back to Strapi heroImage, then placeholder).
// Keyed by the REAL Strapi trailer-type slugs (all 6 now exist in the CMS).
const TRAILER_IMG = {
  'car-hauler': imgCarHauler,
  'dump-trailer': imgDump,
  'enclosed-trailer': imgEnclosed,
  'utility-trailer': imgUtility,
  'equipment-trailer': imgEquipment,
  'gooseneck-trailer': imgGooseneck,
};

// Category art keyed by the REAL Strapi category slugs.
const CATEGORY_IMG = {
  'hitches-towing': catHitches,
  'tie-downs-straps': catStraps,
  'lighting-wiring': catLighting,
  'tires-wheels-axles': catTires,
  'storage-toolboxes': catToolboxes,
  'ramps-gates': catRamps,
  'security-protection': catSecurity,
  'jacks-leveling': catJacks,
  'covers-tarps': catCovers,
  'hydraulics-power': catHydraulics,
  'maintenance-safety': catMaintenance,
};

// ─── Static copy / fallbacks ────────────────────────────────────────────────
// The "Start with your trailer" rail. Slugs map to existing WorkshopTypePage
// routes. If the Strapi type list is present we use its names, else these.
const TRAILER_RAIL = [
  { slug: 'car-hauler-trailer', label: 'Car Hauler' },
  { slug: 'dump-trailer', label: 'Dump' },
  { slug: 'enclosed-trailer', label: 'Enclosed' },
  { slug: 'utility-trailer', label: 'Utility' },
  { slug: 'equipment-trailer', label: 'Equipment' },
  { slug: 'gooseneck-trailer', label: 'Gooseneck' },
];

// The "Build your rental-ready setup" promo cards. Linked to category pages.
const PROMO_CARDS = [
  {
    key: 'secure',
    catSlug: 'tie-downs-straps',
    img: promoSecure,
    heading: 'Secure the load',
    sub: 'Straps, winches, chocks and more to keep cargo in place.',
  },
  {
    key: 'protect',
    catSlug: 'security-protection',
    img: promoProtect,
    heading: 'Protect your investment',
    sub: 'Locks, trackers, and storage for added security and peace of mind.',
  },
  {
    key: 'roadready',
    catSlug: 'tires-wheels-axles',
    img: promoRoadready,
    heading: 'Keep it road-ready',
    sub: 'Tires, lighting, bearings and essentials to minimize downtime.',
  },
];

// ─── Small card components ──────────────────────────────────────────────────
const PlaceholderImg = ({ label, className }) => (
  <div className={`${css.ph} ${className || ''}`} aria-hidden="true">
    <span>{label}</span>
  </div>
);

const TrailerCard = ({ slug, label, img }) => (
  <NamedLink name="WorkshopTypePage" params={{ typeSlug: slug }} className={css.trailerCard}>
    <div className={css.trailerThumb}>
      {img ? <img src={img} alt={label} loading="lazy" /> : <PlaceholderImg label={label} />}
    </div>
    <span className={css.trailerLabel}>
      {label}
      <span className={css.arrow}>→</span>
    </span>
  </NamedLink>
);

const CategoryCard = ({ slug, label, img }) => (
  <NamedLink name="WorkshopCategoryPage" params={{ catSlug: slug }} className={css.catCard}>
    <div className={css.catThumb}>
      {img ? <img src={img} alt={label} loading="lazy" /> : <PlaceholderImg label={label} />}
    </div>
    <span className={css.catLabel}>
      {label}
      <span className={css.arrow}>→</span>
    </span>
  </NamedLink>
);

const ProductCard = ({ p }) => {
  const kicker = p.kicker || (p.category && p.category.name) || 'Trailer Gear';
  const href = p.affiliateUrl || (p.slug ? `/trailer-accessories/product/${p.slug}` : '#');
  const inner = (
    <>
      <div className={css.prodThumb}>
        {p.image ? <img src={p.image} alt={p.name} loading="lazy" /> : <PlaceholderImg label={p.name} />}
      </div>
      <span className={css.prodKicker}>{kicker}</span>
      <h3 className={css.prodName}>{p.name}</h3>
      {p.description ? <p className={css.prodSub}>{p.description}</p> : null}
      <span className={css.prodCta}>
        View gear <span className={css.arrow}>→</span>
      </span>
    </>
  );
  // Prefer internal product page (keeps SEO + the interstitial); fall back to affiliate.
  return p.slug ? (
    <NamedLink name="WorkshopProductPage" params={{ slug: p.slug }} className={css.prodCard}>
      {inner}
    </NamedLink>
  ) : (
    <ExternalLink href={href} className={css.prodCard}>
      {inner}
    </ExternalLink>
  );
};

const WorkshopPageComponent = props => {
  const { scrollingDisabled, config } = props;
  const dispatch = useDispatch();

  const { types, categories, featured, homeInProgress } = useSelector(state => state.workshop);

  // Client-side navigation re-fetch (loadData covers SSR / first paint).
  useEffect(() => {
    if (!types || types.length === 0) {
      dispatch(loadWorkshopHome());
    }
  }, []); // eslint-disable-line

  const marketplaceName = config?.marketplaceName || 'BigLoads';
  const siteUrl = config?.marketplaceRootURL || 'https://bigloads.com';
  const pageUrl = `${siteUrl}/trailer-accessories`;

  const seoTitle = 'Trailer Accessories & Gear — The Workshop | BigLoads';
  const seoDescription =
    'Trailer parts, gear and upgrades for your rental business. Shop hitches, tie-downs, lighting, tires, storage and security gear by trailer type or category.';

  // ── Structured data (preserved) ──────────────────────────────────────────
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Trailer Accessories', item: pageUrl },
    ],
  };
  const collectionSchema = {
    '@type': 'CollectionPage',
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    name: seoTitle,
    description: seoDescription,
    isPartOf: { '@type': 'WebSite', name: marketplaceName, url: siteUrl },
  };
  const schemas = [collectionSchema, breadcrumbSchema];

  // ── Data-backed lists with graceful fallbacks ────────────────────────────
  const sortedCats = [...(categories || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const typeBySlug = {};
  (types || []).forEach(t => { typeBySlug[t.slug] = t; });

  // "Start with your trailer" rail: driven by REAL Strapi types only (so every
  // link routes to a page that exists). Falls back to the static rail only until
  // the type list has loaded.
  const trailerCards =
    types && types.length
      ? [...types]
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map(t => ({
            slug: t.slug,
            label: t.name,
            img: TRAILER_IMG[t.slug] || t.heroImage || null,
          }))
      : TRAILER_RAIL.map(r => ({ slug: r.slug, label: r.label, img: TRAILER_IMG[r.slug] || null }));

  // "Everything your trailer needs": ALL categories from Strapi (ordered). The
  // hero's "Shop all categories" CTA scrolls here, so every category we offer
  // must be visible (previously capped at 8, which hid maintenance/hydraulics/
  // jacks and left them undiscoverable from the homepage grid).
  const categoryCards = sortedCats.map(c => ({
    slug: c.slug,
    label: c.name,
    img: CATEGORY_IMG[c.slug] || c.heroImage || null,
  }));

  const featuredProducts = (featured || []).slice(0, 4);

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={seoTitle}
      description={seoDescription}
      canonicalUrl={pageUrl}
      schema={schemas}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={true} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        {/* Workshop secondary bar: search (stub) + category chips */}
        <WorkshopSubnav categories={sortedCats} css={css} />

        <div className={css.page}>
          {/* HERO */}
          <section className={css.hero}>
            <div className={css.heroText}>
              <span className={css.kicker}>The Workshop</span>
              <h1 className={css.heroTitle}>Outfit your trailer. Get rental-ready.</h1>
              <p className={css.heroSub}>
                Every strap, hitch, light, and lock your rig needs to haul hard and earn its
                keep. We pull the gear that trailer owners actually trust, match it to the way
                you work, and send you straight to the best place to buy it. No fluff, no
                guesswork. Just the stuff that keeps your trailer loaded, legal, and on the
                road.
              </p>
              <a href="#categories" className={`${css.btnPrimary} ${css.heroBtn}`}>
                Shop all categories <span className={css.arrow}>→</span>
              </a>
            </div>
            <div className={css.heroImg}>
              <img
                src={heroImg}
                alt="Flatbed trailer loaded with a toolbox, spare tire, winch, jacks, ratchet straps, ramps and lights at sunset"
              />
            </div>
          </section>

          {/* TRUST STRIP — ownership-focused (Your Business | Your Trailers | Your Gear) */}
          <section className={css.trust}>
            <div className={css.trustItem}>
              <span className={css.trustLabel}>Your Business</span>
              <span className={css.trustSub}>
                Gear up to haul more, charge more, and keep renters coming back.
              </span>
            </div>
            <div className={css.trustItem}>
              <span className={css.trustLabel}>Your Trailers</span>
              <span className={css.trustSub}>
                Outfit every rig in your fleet with the hardware that earns its keep.
              </span>
            </div>
            <div className={css.trustItem}>
              <span className={css.trustLabel}>Your Gear</span>
              <span className={css.trustSub}>
                Hand-picked straps, locks, and lighting trusted by working owners.
              </span>
            </div>
          </section>

          {/* START WITH YOUR TRAILER */}
          <section className={css.section}>
            <h2 className={css.sectionTitle}>Start with your trailer</h2>
            <div className={css.trailerGrid}>
              {trailerCards.map(t => (
                <TrailerCard key={t.slug} {...t} />
              ))}
            </div>
          </section>

          {/* EVERYTHING YOUR TRAILER NEEDS */}
          <section className={css.section} id="categories">
            <h2 className={css.sectionTitle}>Everything your trailer needs</h2>
            {categoryCards.length > 0 ? (
              <div className={css.catGrid}>
                {categoryCards.map(c => (
                  <CategoryCard key={c.slug} {...c} />
                ))}
              </div>
            ) : (
              <div className={css.loadingState}>
                {homeInProgress ? 'Loading categories…' : 'Categories coming soon.'}
              </div>
            )}
          </section>

          {/* BUILD YOUR RENTAL-READY SETUP */}
          <section className={css.section}>
            <h2 className={css.sectionTitle}>Build your rental-ready setup</h2>
            <div className={css.promoGrid}>
              {PROMO_CARDS.map(pc => (
                <NamedLink
                  key={pc.key}
                  name="WorkshopCategoryPage"
                  params={{ catSlug: pc.catSlug }}
                  className={css.promoCard}
                >
                  {pc.img ? (
                    <div className={css.promoThumb}>
                      <img src={pc.img} alt={pc.heading} loading="lazy" />
                    </div>
                  ) : (
                    <PlaceholderImg label={pc.heading} className={css.phPromo} />
                  )}
                  <div className={css.promoBody}>
                    <h3 className={css.promoHeading}>{pc.heading}</h3>
                    <p className={css.promoSub}>{pc.sub}</p>
                    <span className={css.promoCta}>
                      Explore essentials <span className={css.arrow}>→</span>
                    </span>
                  </div>
                </NamedLink>
              ))}
            </div>
          </section>

          {/* GEAR WORTH A CLOSER LOOK (featured products; hidden if none) */}
          {featuredProducts.length > 0 ? (
            <section className={css.section}>
              <div className={css.sectionHead}>
                <h2 className={css.sectionTitle}>Gear worth a closer look</h2>
              </div>
              <div className={css.prodGrid}>
                {featuredProducts.map(p => (
                  <ProductCard key={p.id || p.slug} p={p} />
                ))}
              </div>
            </section>
          ) : null}

          {/* OWNER GUIDES CTA BANNER */}
          <section className={css.ownerBanner}>
            <img src={ownerBannerImg} alt="Truck towing an enclosed trailer at dusk" className={css.ownerBannerImg} />
            <div className={css.ownerBannerInner}>
              <h2 className={css.ownerTitle}>More rentals. Fewer headaches.</h2>
              <p className={css.ownerSub}>
                Practical checklists and guides for trailer rental owners.
              </p>
              <NamedLink name="ToolboxIndexPage" className={css.btnPrimary}>
                Explore owner guides <span className={css.arrow}>→</span>
              </NamedLink>
            </div>
          </section>

          {/* Affiliate disclosure */}
          <p className={css.disclosure}>Some links may earn BigLoads a commission.</p>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

WorkshopPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
};

const EnhancedWorkshopPage = props => {
  const config = useConfiguration();
  return <WorkshopPageComponent config={config} {...props} />;
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const WorkshopPage = compose(connect(mapStateToProps))(EnhancedWorkshopPage);

// SSR loader (named export for pageDataLoadingAPI)
export const loadData = (params, search, config) => dispatch => {
  return dispatch(loadWorkshopHome());
};

export default WorkshopPage;
